import { useEffect, useState, useMemo } from "react";
import axios from "axios";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const FIELDS = ["fullName", "phone", "street", "city", "state", "pincode"];
const FIELD_LABELS = {
  fullName: "Full Name", phone: "Phone",
  street: "Street Address", city: "City",
  state: "State", pincode: "Pincode",
};

const ProfileIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/>
    <circle cx="12" cy="7" r="4"/>
  </svg>
);
const AddressIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 0118 0z"/>
    <circle cx="12" cy="10" r="3"/>
  </svg>
);
const OrdersIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z"/>
    <line x1="3" y1="6" x2="21" y2="6"/>
    <path d="M16 10a4 4 0 01-8 0"/>
  </svg>
);
const PinIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 0118 0z"/>
    <circle cx="12" cy="10" r="3"/>
  </svg>
);

function Dashboard({ cart = [] }) {
  const [user, setUser]           = useState(null);
  const [addresses, setAddresses] = useState([]);
  const [orders, setOrders]       = useState([]);
  const [loading, setLoading]     = useState(true);

  const [editingUser, setEditingUser] = useState(false);
  const [userName, setUserName]       = useState("");
  const [userEmail, setUserEmail]     = useState("");

  const [showAddAddress, setShowAddAddress]   = useState(false);
  const [newAddress, setNewAddress]           = useState({ fullName: "", phone: "", street: "", city: "", state: "", pincode: "" });
  const [editingAddressId, setEditingAddressId] = useState(null);
  const [editAddress, setEditAddress]         = useState({});

  const [activeTab, setActiveTab] = useState("profile");

  const token   = localStorage.getItem("token");
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  useEffect(() => {
    axios.get(`${API_BASE_URL}/dashboard`, { headers })
      .then(res => {
        setUser(res.data.user);
        setUserName(res.data.user.name);
        setUserEmail(res.data.user.email);
        setAddresses(res.data.addresses);
        setOrders(res.data.orders);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [headers]);

  const handleUpdateUser = async () => {
    await axios.put(`${API_BASE_URL}/user`, { name: userName, email: userEmail }, { headers });
    if (userEmail !== user.email) {
      localStorage.removeItem("token");
      localStorage.removeItem("cart");
      alert("Email updated. Please log in again.");
      window.location.href = "/login";
    } else {
      setUser({ ...user, name: userName });
      setEditingUser(false);
    }
  };

  const handleAddAddress = async () => {
    const res = await axios.post(`${API_BASE_URL}/address`, newAddress, { headers });
    setAddresses([...addresses, res.data]);
    setNewAddress({ fullName: "", phone: "", street: "", city: "", state: "", pincode: "" });
    setShowAddAddress(false);
  };

  const handleUpdateAddress = async (id) => {
    const res = await axios.put(`${API_BASE_URL}/address/${id}`, editAddress, { headers });
    setAddresses(addresses.map(a => a.id === id ? res.data : a));
    setEditingAddressId(null);
  };

  const handleDeleteAddress = async (id) => {
    await axios.delete(`${API_BASE_URL}/address/${id}`, { headers });
    setAddresses(addresses.filter(a => a.id !== id));
  };

  const NAV = [
    { key: "profile",   label: "Profile",   icon: <ProfileIcon />,  count: null },
    { key: "addresses", label: "Addresses", icon: <AddressIcon />, count: addresses.length },
    { key: "orders",    label: "Orders",    icon: <OrdersIcon />,   count: orders.length },
  ];

  if (loading) return (
    <div className="tc-loading">
      <div className="tc-spinner" />
      <p style={{ fontSize: 12, letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--text-muted)", marginTop: 12 }}>
        Loading profile...
      </p>
    </div>
  );

  return (
    <div className="tc-page min-h-screen" style={{ animation: "dbFadeIn 0.4s ease both" }}>
      <style>{`
        @keyframes dbFadeIn {
          from { opacity: 0; transform: translateY(14px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes dbSlideIn {
          from { opacity: 0; transform: translateY(16px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes dbFormIn {
          from { opacity: 0; transform: translateY(-8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .db-nav-item {
          width: 100%; display: flex; align-items: center; gap: 10px;
          padding: 10px 14px; border-radius: var(--radius-md);
          border: 1px solid transparent; background: transparent;
          cursor: pointer; text-align: left;
          transition: background 0.18s ease, border-color 0.18s ease, color 0.18s ease;
        }
        .db-nav-item:hover { background: var(--bg-hover); }
        .db-nav-item.active {
          background: var(--accent-bg) !important;
          border-color: var(--accent) !important;
        }
        .db-addr-card {
          transition: border-color 0.18s ease, background 0.18s ease;
        }
        .db-addr-card:hover { border-color: var(--border-default) !important; }
        .db-order-row { transition: background 0.15s ease; }
        .db-order-row:hover { background: var(--bg-hover) !important; }
        .db-btn-danger-sm:hover { background: var(--danger-bg) !important; border-color: var(--danger-border) !important; color: var(--danger-text) !important; }
        .db-signout:hover { background: var(--danger-bg) !important; border-color: var(--danger-border) !important; color: var(--danger-text) !important; }
        .db-tab-content { animation: dbSlideIn 0.25s ease both; }
      `}</style>

      <div className="tc-container" style={{ maxWidth: 1000 }}>

        {/* ── Page heading ── */}
        <div style={{
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 12,
          marginBottom: 32,
          animation: "dbSlideIn 0.35s ease both",
        }}>
          <h1 style={{
            fontFamily: "var(--font-display)",
            fontSize: "clamp(32px, 5vw, 48px)",
            letterSpacing: "0.04em",
            color: "var(--text-primary)",
            lineHeight: 1,
          }}>
            MY ACCOUNT
          </h1>
          <div style={{ display: "flex", gap: 20 }}>
            {[
              { label: "Addresses", value: addresses.length },
              { label: "Orders", value: orders.length },
            ].map(stat => (
              <div key={stat.label} style={{ textAlign: "right" }}>
                <p style={{
                  fontFamily: "var(--font-display)",
                  fontSize: 28,
                  lineHeight: 1,
                  color: "var(--accent)",
                  letterSpacing: "0.04em",
                }}>
                  {stat.value}
                </p>
                <p style={{ fontSize: 10, color: "var(--text-muted)", letterSpacing: "0.1em", textTransform: "uppercase", marginTop: 2 }}>
                  {stat.label}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* ── Two-panel layout ── */}
        <div style={{ display: "flex", gap: 24, alignItems: "flex-start", flexWrap: "wrap" }}>

          {/* ── Sidebar ── */}
          <div style={{
            width: 210,
            flexShrink: 0,
            display: "flex",
            flexDirection: "column",
            gap: 4,
            animation: "dbSlideIn 0.38s ease 0.04s both",
            position: "sticky",
            top: 24,
          }}>

            {/* Avatar block */}
            <div style={{
              padding: "20px 16px",
              borderRadius: "var(--radius-md)",
              border: "1px solid var(--border-subtle)",
              background: "var(--bg-card)",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 10,
              marginBottom: 8,
              textAlign: "center",
            }}>
              <div style={{
                width: 60,
                height: 60,
                borderRadius: "50%",
                background: "var(--accent-bg)",
                border: "2px solid var(--accent)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontFamily: "var(--font-display)",
                fontSize: 28,
                letterSpacing: "0.04em",
                color: "var(--accent)",
                boxShadow: "0 0 16px rgba(16,185,129,0.2)",
              }}>
                {user?.name?.charAt(0).toUpperCase()}
              </div>
              <div>
                <p style={{ fontSize: 14, fontWeight: 500, color: "var(--text-primary)", marginBottom: 3 }}>
                  {user?.name}
                </p>
                <p style={{ fontSize: 10, color: "var(--text-muted)", fontFamily: "var(--font-mono)", letterSpacing: "0.04em", wordBreak: "break-all" }}>
                  {user?.email}
                </p>
              </div>
            </div>

            {/* Nav */}
            <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
              {NAV.map(item => (
                <button
                  key={item.key}
                  className={`db-nav-item${activeTab === item.key ? " active" : ""}`}
                  onClick={() => setActiveTab(item.key)}
                >
                  <span style={{ color: activeTab === item.key ? "var(--accent)" : "var(--text-muted)", flexShrink: 0 }}>
                    {item.icon}
                  </span>
                  <span style={{
                    fontSize: 12,
                    color: activeTab === item.key ? "var(--accent)" : "var(--text-secondary)",
                    fontWeight: activeTab === item.key ? 500 : 400,
                    flex: 1,
                  }}>
                    {item.label}
                  </span>
                  {item.count !== null && item.count > 0 && (
                    <span style={{
                      fontSize: 9,
                      fontFamily: "var(--font-mono)",
                      padding: "2px 6px",
                      borderRadius: "var(--radius-pill)",
                      background: activeTab === item.key ? "var(--accent)" : "var(--bg-secondary)",
                      color: activeTab === item.key ? "var(--bg-primary)" : "var(--text-muted)",
                      border: `1px solid ${activeTab === item.key ? "transparent" : "var(--border-subtle)"}`,
                    }}>
                      {item.count}
                    </span>
                  )}
                </button>
              ))}
            </div>

            {/* Sign out */}
            <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px solid var(--border-subtle)" }}>
              <button
                className="db-signout"
                onClick={() => {
                  localStorage.removeItem("token");
                  localStorage.removeItem("cart");
                  window.location.href = "/login";
                }}
                style={{
                  width: "100%",
                  padding: "9px 14px",
                  borderRadius: "var(--radius-md)",
                  border: "1px solid var(--border-subtle)",
                  background: "transparent",
                  cursor: "pointer",
                  fontSize: 10,
                  letterSpacing: "0.12em",
                  textTransform: "uppercase",
                  color: "var(--text-muted)",
                  fontFamily: "var(--font-mono)",
                  transition: "background 0.18s ease, border-color 0.18s ease, color 0.18s ease",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 7,
                }}
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4M16 17l5-5-5-5M21 12H9"/>
                </svg>
                Sign out
              </button>
            </div>
          </div>

          {/* ── Main content ── */}
          <div style={{ flex: "1 1 320px", minWidth: 0 }}>

            {/* ── Profile tab ── */}
            {activeTab === "profile" && (
              <div key="profile" className="db-tab-content">
                <div className="tc-card" style={{ padding: 0, overflow: "hidden" }}>
                  <div style={{
                    padding: "14px 20px",
                    borderBottom: "1px solid var(--border-subtle)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}>
                    <span style={{ fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--text-muted)", fontFamily: "var(--font-mono)" }}>
                      Account Details
                    </span>
                    {!editingUser && (
                      <button
                        className="tc-btn tc-btn-secondary"
                        onClick={() => setEditingUser(true)}
                        style={{ padding: "5px 14px", fontSize: 10, letterSpacing: "0.1em" }}
                      >
                        Edit
                      </button>
                    )}
                  </div>

                  <div style={{ padding: "20px" }}>
                    {editingUser ? (
                      <div style={{ animation: "dbFormIn 0.2s ease both" }}>
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 16 }}>
                          {[
                            { label: "Name", value: userName, setter: setUserName, placeholder: "Your name" },
                            { label: "Email", value: userEmail, setter: setUserEmail, placeholder: "Your email" },
                          ].map(f => (
                            <div key={f.label} style={{ gridColumn: "1 / -1" }}>
                              <label style={{ fontSize: 10, letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--text-muted)", fontFamily: "var(--font-mono)", display: "block", marginBottom: 6 }}>
                                {f.label}
                              </label>
                              <input
                                className="tc-input"
                                value={f.value}
                                onChange={e => f.setter(e.target.value)}
                                placeholder={f.placeholder}
                                style={{ fontSize: 13, width: "100%" }}
                              />
                            </div>
                          ))}
                        </div>
                        <div style={{ display: "flex", gap: 8 }}>
                          <button className="tc-btn tc-btn-primary" onClick={handleUpdateUser} style={{ fontSize: 11, padding: "8px 20px" }}>
                            Save changes
                          </button>
                          <button className="tc-btn tc-btn-secondary" onClick={() => setEditingUser(false)} style={{ fontSize: 11, padding: "8px 20px" }}>
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1, background: "var(--border-subtle)", border: "1px solid var(--border-subtle)", borderRadius: "var(--radius-sm)", overflow: "hidden" }}>
                        {[
                          { label: "Name", value: user?.name },
                          { label: "Email", value: user?.email },
                          { label: "Role", value: user?.role?.toLowerCase() || "customer" },
                          { label: "Total Orders", value: orders.length },
                        ].map(field => (
                          <div key={field.label} style={{ padding: "14px 18px", background: "var(--bg-card)" }}>
                            <p style={{ fontSize: 9, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--text-muted)", fontFamily: "var(--font-mono)", marginBottom: 6 }}>
                              {field.label}
                            </p>
                            <p style={{ fontSize: 14, color: "var(--text-primary)", fontWeight: 400, wordBreak: "break-word" }}>
                              {field.value}
                            </p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* ── Addresses tab ── */}
            {activeTab === "addresses" && (
              <div key="addresses" className="db-tab-content">
                <div className="tc-card" style={{ padding: 0, overflow: "hidden" }}>
                  <div style={{
                    padding: "14px 20px",
                    borderBottom: "1px solid var(--border-subtle)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}>
                    <span style={{ fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--text-muted)", fontFamily: "var(--font-mono)" }}>
                      Saved Addresses
                    </span>
                    <button
                      className="tc-btn tc-btn-secondary"
                      onClick={() => { setShowAddAddress(!showAddAddress); setEditingAddressId(null); }}
                      style={{ padding: "5px 14px", fontSize: 10, letterSpacing: "0.1em" }}
                    >
                      {showAddAddress ? "Cancel" : "+ Add new"}
                    </button>
                  </div>

                  <div style={{ padding: "20px", display: "flex", flexDirection: "column", gap: 12 }}>

                    {/* Add form */}
                    {showAddAddress && (
                      <div style={{
                        padding: "16px",
                        borderRadius: "var(--radius-sm)",
                        border: "1px solid var(--accent)",
                        background: "var(--accent-bg)",
                        animation: "dbFormIn 0.2s ease both",
                        marginBottom: 4,
                      }}>
                        <p style={{ fontSize: 10, letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--accent)", fontFamily: "var(--font-mono)", marginBottom: 14 }}>
                          New Address
                        </p>
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 14 }}>
                          {FIELDS.map(field => (
                            <div key={field} style={{ gridColumn: field === "street" ? "1 / -1" : undefined }}>
                              <label style={{ fontSize: 9, letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--text-muted)", fontFamily: "var(--font-mono)", display: "block", marginBottom: 5 }}>
                                {FIELD_LABELS[field]}
                              </label>
                              <input
                                className="tc-input"
                                placeholder={FIELD_LABELS[field]}
                                value={newAddress[field]}
                                onChange={e => setNewAddress({ ...newAddress, [field]: e.target.value })}
                                style={{ fontSize: 12, width: "100%" }}
                              />
                            </div>
                          ))}
                        </div>
                        <div style={{ display: "flex", gap: 8 }}>
                          <button className="tc-btn tc-btn-primary" onClick={handleAddAddress} style={{ fontSize: 11, padding: "8px 20px" }}>
                            Save address
                          </button>
                          <button className="tc-btn tc-btn-secondary" onClick={() => setShowAddAddress(false)} style={{ fontSize: 11, padding: "8px 20px" }}>
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Empty state */}
                    {addresses.length === 0 && !showAddAddress && (
                      <div style={{ padding: "32px 0", textAlign: "center" }}>
                        <div style={{ color: "var(--text-muted)", marginBottom: 10 }}>
                          <AddressIcon />
                        </div>
                        <p style={{ fontSize: 12, color: "var(--text-muted)", letterSpacing: "0.06em" }}>
                          No saved addresses yet
                        </p>
                      </div>
                    )}

                    {/* Address cards */}
                    {addresses.map(a => (
                      <div key={a.id}>
                        {editingAddressId === a.id ? (
                          <div style={{
                            padding: "16px",
                            borderRadius: "var(--radius-sm)",
                            border: "1px solid var(--accent)",
                            background: "var(--accent-bg)",
                            animation: "dbFormIn 0.2s ease both",
                          }}>
                            <p style={{ fontSize: 10, letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--accent)", fontFamily: "var(--font-mono)", marginBottom: 14 }}>
                              Edit Address
                            </p>
                            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 14 }}>
                              {FIELDS.map(field => (
                                <div key={field} style={{ gridColumn: field === "street" ? "1 / -1" : undefined }}>
                                  <label style={{ fontSize: 9, letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--text-muted)", fontFamily: "var(--font-mono)", display: "block", marginBottom: 5 }}>
                                    {FIELD_LABELS[field]}
                                  </label>
                                  <input
                                    className="tc-input"
                                    placeholder={FIELD_LABELS[field]}
                                    value={editAddress[field] || ""}
                                    onChange={e => setEditAddress({ ...editAddress, [field]: e.target.value })}
                                    style={{ fontSize: 12, width: "100%" }}
                                  />
                                </div>
                              ))}
                            </div>
                            <div style={{ display: "flex", gap: 8 }}>
                              <button className="tc-btn tc-btn-primary" onClick={() => handleUpdateAddress(a.id)} style={{ fontSize: 11, padding: "8px 20px" }}>
                                Save changes
                              </button>
                              <button className="tc-btn tc-btn-secondary" onClick={() => setEditingAddressId(null)} style={{ fontSize: 11, padding: "8px 20px" }}>
                                Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div
                            className="db-addr-card"
                            style={{
                              padding: "14px 16px",
                              borderRadius: "var(--radius-sm)",
                              border: "1px solid var(--border-subtle)",
                              background: "var(--bg-secondary)",
                              display: "flex",
                              alignItems: "flex-start",
                              justifyContent: "space-between",
                              gap: 12,
                            }}
                          >
                            <div style={{ display: "flex", gap: 12, alignItems: "flex-start", flex: 1, minWidth: 0 }}>
                              <div style={{ color: "var(--accent)", opacity: 0.7, marginTop: 1, flexShrink: 0 }}>
                                <PinIcon />
                              </div>
                              <div>
                                <p style={{ fontSize: 13, fontWeight: 500, color: "var(--text-primary)", marginBottom: 4 }}>
                                  {a.fullName}
                                </p>
                                <p style={{ fontSize: 11, color: "var(--text-secondary)", lineHeight: 1.6 }}>
                                  {a.street}
                                </p>
                                <p style={{ fontSize: 11, color: "var(--text-secondary)" }}>
                                  {a.city}, {a.state} — {a.pincode}
                                </p>
                                {a.phone && (
                                  <p style={{ fontSize: 10, color: "var(--text-muted)", fontFamily: "var(--font-mono)", marginTop: 4 }}>
                                    {a.phone}
                                  </p>
                                )}
                              </div>
                            </div>
                            <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                              <button
                                className="tc-btn tc-btn-secondary"
                                onClick={() => { setEditingAddressId(a.id); setEditAddress(a); setShowAddAddress(false); }}
                                style={{ padding: "5px 12px", fontSize: 10 }}
                              >
                                Edit
                              </button>
                              <button
                                className="db-btn-danger-sm tc-btn tc-btn-secondary"
                                onClick={() => handleDeleteAddress(a.id)}
                                style={{ padding: "5px 12px", fontSize: 10, transition: "background 0.18s, border-color 0.18s, color 0.18s" }}
                              >
                                Delete
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* ── Orders tab ── */}
            {activeTab === "orders" && (
              <div key="orders" className="db-tab-content">
                <div className="tc-card" style={{ padding: 0, overflow: "hidden" }}>
                  <div style={{
                    padding: "14px 20px",
                    borderBottom: "1px solid var(--border-subtle)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}>
                    <span style={{ fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--text-muted)", fontFamily: "var(--font-mono)" }}>
                      Order History
                    </span>
                    <span style={{ fontSize: 10, fontFamily: "var(--font-mono)", color: "var(--accent)", background: "var(--accent-bg)", border: "1px solid var(--accent)", borderRadius: "var(--radius-pill)", padding: "2px 8px", letterSpacing: "0.06em" }}>
                      {orders.length} total
                    </span>
                  </div>

                  {orders.length === 0 ? (
                    <div style={{ padding: "48px 20px", textAlign: "center" }}>
                      <div style={{ color: "var(--text-muted)", marginBottom: 10, display: "flex", justifyContent: "center" }}>
                        <OrdersIcon />
                      </div>
                      <p style={{ fontSize: 12, color: "var(--text-muted)", letterSpacing: "0.06em" }}>
                        No orders placed yet
                      </p>
                    </div>
                  ) : (
                    <div>
                      {orders.map((o, i) => (
                        <div
                          key={o.id}
                          className="db-order-row"
                          style={{
                            padding: "14px 20px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 12,
                            borderBottom: i < orders.length - 1 ? "1px solid var(--border-subtle)" : "none",
                            background: "transparent",
                          }}
                        >
                          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                            <div style={{
                              width: 6,
                              height: 6,
                              borderRadius: "50%",
                              background: o.paymentStatus === "PAID" ? "var(--accent)" : "var(--warning-text)",
                              boxShadow: `0 0 6px ${o.paymentStatus === "PAID" ? "var(--accent)" : "var(--warning-text)"}`,
                              flexShrink: 0,
                            }} />
                            <div>
                              <p style={{ fontSize: 12, color: "var(--text-primary)", fontFamily: "var(--font-mono)", marginBottom: 3 }}>
                                #{String(o.id).padStart(6, "0")}
                              </p>
                              <p style={{ fontSize: 10, color: "var(--text-muted)" }}>
                                {new Date(o.orderDate).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                              </p>
                            </div>
                          </div>
                          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                            {o.amount && (
                              <span style={{
                                fontFamily: "var(--font-display)",
                                fontSize: 20,
                                letterSpacing: "0.04em",
                                color: "var(--text-primary)",
                              }}>
                                ₹{Number(o.amount).toLocaleString("en-IN")}
                              </span>
                            )}
                            <span style={{
                              fontSize: 9,
                              fontFamily: "var(--font-mono)",
                              letterSpacing: "0.1em",
                              textTransform: "uppercase",
                              padding: "3px 8px",
                              borderRadius: "var(--radius-pill)",
                              background: o.paymentStatus === "PAID" ? "var(--success-bg)" : "var(--warning-bg)",
                              color: o.paymentStatus === "PAID" ? "var(--success-text)" : "var(--warning-text)",
                              border: `1px solid ${o.paymentStatus === "PAID" ? "var(--success-border)" : "var(--warning-border)"}`,
                            }}>
                              {o.paymentStatus || "Pending"}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

          </div>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;
